import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PASSWORD_MAX_LENGTH } from "@/lib/auth-error";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { CaretDown, CircleNotch, Key } from "@phosphor-icons/react";

interface LoginAltMethodsProps {
  loading: boolean;
  onGoogle: () => void;
  onPasswordSubmit: (email: string, password: string) => void;
  onClearError: () => void;
}

export function LoginAltMethods({
  loading,
  onGoogle,
  onPasswordSubmit,
  onClearError,
}: LoginAltMethodsProps) {
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  return (
    <>
      {/* Divider */}
      <div className="my-6 flex items-center gap-3" aria-hidden>
        <span className="h-px flex-1 bg-border" />
        <span className="text-xs uppercase tracking-wider text-muted-foreground">
          {t("login.or")}
        </span>
        <span className="h-px flex-1 bg-border" />
      </div>

      {/* Google */}
      <Button
        variant="outline"
        size="lg"
        onClick={onGoogle}
        disabled={loading}
        className="h-12 w-full rounded-full border-border bg-white/80 text-sm font-medium hover:bg-white"
      >
        <GoogleIcon className="size-4" />
        {t("login.continueGoogle")}
      </Button>

      {/* Password disclosure */}
      <div className="mt-4">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => {
            setShowPassword(!showPassword);
            onClearError();
          }}
          aria-expanded={showPassword}
          className="w-full rounded-full text-xs text-muted-foreground hover:bg-white/60 hover:text-foreground"
        >
          <Key className="size-3.5" />
          {t("login.passwordLogin")}
          <CaretDown
            className={cn(
              "size-3.5 transition-transform duration-300",
              showPassword && "rotate-180",
            )}
          />
        </Button>

        <div
          className={cn(
            "grid transition-all duration-300 ease-in-out",
            showPassword
              ? "mt-3 grid-rows-[1fr] opacity-100"
              : "grid-rows-[0fr] opacity-0",
          )}
        >
          <div className="overflow-hidden">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                onPasswordSubmit(email, password);
              }}
              className="flex flex-col gap-3 rounded-2xl border bg-white/60 p-4"
              style={{ borderColor: "var(--color-warm-border)" }}
            >
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="pw-email" className="text-xs">
                  {t("login.emailLabel")}
                </Label>
                <Input
                  id="pw-email"
                  type="email"
                  placeholder={t("login.emailPlaceholder")}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  className="h-11 rounded-xl bg-white"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="pw-password" className="text-xs">
                  {t("login.passwordLabel")}
                </Label>
                <Input
                  id="pw-password"
                  type="password"
                  placeholder={t("login.passwordPlaceholder")}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  maxLength={PASSWORD_MAX_LENGTH}
                  autoComplete="current-password"
                  className="h-11 rounded-xl bg-white"
                />
              </div>
              <Link
                to="/forgot-password"
                className="text-xs text-muted-foreground hover:text-foreground hover:underline"
              >
                {t("login.forgotPassword")}
              </Link>
              <Button
                type="submit"
                disabled={loading}
                className="h-11 w-full rounded-full"
              >
                {loading ? (
                  <CircleNotch className="animate-spin" data-icon="inline-start" />
                ) : null}
                {loading ? t("login.loggingIn") : t("login.loginButton")}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}

function GoogleIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className}>
      <path
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
        fill="#4285F4"
      />
      <path
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        fill="#34A853"
      />
      <path
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        fill="#FBBC05"
      />
      <path
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        fill="#EA4335"
      />
    </svg>
  );
}
